import React, {
  ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent, ReactNode, useCallback, useEffect, useId,
  useMemo, useRef, useState,
} from 'react';
import { Button, Dropdown, DropdownMenuProps } from 'react-bootstrap';

import { MyStudiesFilterOption, filterOptions } from 'helper/flux/myStudiesFilterOptions';

import './index.scss';

/**
 * Estado das opções: carregando (também no "Tentar de novo"), com erro (a mensagem vem pronta de
 * quem chama) ou prontas.
 */
export type OptionsPopoverStatus = 'loading' | 'error' | 'ready';

export interface OptionsPopoverProps {
  /** Aberto ou fechado: quem chama guarda (o UserPopover só busca a lista depois de aberto). */
  open: boolean;
  /**
   * Pedido para abrir (`true`) ou fechar (`false`): botão, Esc, Tab e Shift+Tab para fora, clique
   * fora e a escolha de uma opção.
   */
  onOpenChange: (open: boolean) => void;
  /** Classes do botão que abre o popover. */
  toggleClassName: string;
  /** Conteúdo do botão (texto e chevron). */
  toggleContent: ReactNode;
  /** `id` do botão, para quem compõe o nome dele por `aria-labelledby`. */
  toggleId?: string;
  /** Nome acessível do botão, quando o texto dele não basta (ex.: "Criado por Usuário"). */
  toggleAriaLabel?: string;
  /** Ids que compõem o nome do botão (ex.: o rótulo do campo e o próprio botão). */
  toggleAriaLabelledBy?: string;
  /** Lado do botão em que o popover se alinha: Empresa à esquerda, Usuário à direita. */
  align?: 'start' | 'end';
  /** Classe a mais no cartão (a largura de cada filtro). */
  menuClassName?: string;
  /** Nome acessível do campo de busca. */
  searchLabel: string;
  /** Texto de exemplo do campo de busca (.dc.html:160 e 210). */
  searchPlaceholder: string;
  /** Nome acessível da lista. */
  listLabel: string;
  /**
   * Opções na ordem da lista, antes da busca (o popover filtra com `filterOptions`). Valem em
   * todos os estados: quem chama decide o que existe enquanto carrega ou com erro (o
   * CompanyFilter mantém os dois itens fixos; o UserPopover, nenhum).
   */
  options: readonly MyStudiesFilterOption[];
  /** Chave da opção aplicada: fundo de selecionada, e o destaque abre nela. */
  selectedKey: string | null;
  /** Recebe a chave escolhida; o popover já fecha e devolve o foco ao botão. */
  onSelect: (key: string) => void;
  /** Estado das opções: escolhe a mensagem da região de status e o "Tentar de novo". */
  status: OptionsPopoverStatus;
  /** Mensagem do `status: 'loading'` (1º pedido e "Tentar de novo"). */
  loadingMessage: string;
  /** Lista vazia (nada no escopo ou nada que case com a busca). */
  emptyMessage: string;
  /** Mensagem do erro (`status: 'error'`), já com o tipo de falha. */
  errorMessage?: string;
  /** "Tentar de novo" no erro; sem ele (ex.: 403), só a mensagem. */
  onRetry?: () => void;
}

/** O popover abre a 6 px do botão, como no protótipo (.dc.html:159 e 209). */
const MENU_POPPER_CONFIG: DropdownMenuProps['popperConfig'] = {
  modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
};

/** O que o conteúdo do popover usa; o resto é do botão e do cartão. */
type ListKey = 'searchLabel' | 'searchPlaceholder' | 'listLabel' | 'options' | 'selectedKey';
type StateKey = 'onSelect' | 'status' | 'loadingMessage' | 'emptyMessage' | 'errorMessage' | 'onRetry';
type PanelProps = Pick<OptionsPopoverProps, ListKey | StateKey>;

/**
 * Conteúdo do popover aberto. Só existe com ele aberto: a busca e o destaque recomeçam a cada
 * abertura, como no protótipo (que limpa a busca ao escolher, .dc.html:661 e 666).
 *
 * Padrão APG de combobox com lista: o foco fica no campo de busca, e as setas movem o destaque
 * (`aria-activedescendant` e `aria-selected`); Enter escolhe. O anel do destaque só aparece
 * depois da 1ª seta: quem abre com o mouse vê só o fundo da aplicada. Home, End, PageUp e PageDown
 * não mexem no destaque: Home e End ficam com o cursor do texto, como num campo comum.
 *
 * A mensagem (carregando, vazio ou erro) fica numa região `status` e também descreve a busca
 * (`aria-describedby`): o painel monta já com ela, e uma região viva que nasce preenchida costuma
 * não ser lida.
 */
function OptionsPanel({
  searchLabel,
  searchPlaceholder,
  listLabel,
  options,
  selectedKey,
  onSelect,
  status,
  loadingMessage,
  emptyMessage,
  errorMessage,
  onRetry,
  onBackTab,
}: PanelProps & { onBackTab: () => void }) {
  const listboxId = useId();
  const messageId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  // `null` = a primeira da lista; abre na aplicada
  const [activeKey, setActiveKey] = useState<string | null>(selectedKey);
  // O anel do destaque é do teclado: acende na 1ª seta
  const [keyboardActive, setKeyboardActive] = useState(false);

  const visible = useMemo(() => filterOptions(options, query), [options, query]);
  const keptIndex = visible.findIndex((item) => item.key === activeKey);
  const activeIndex = keptIndex >= 0 ? keptIndex : Math.min(0, visible.length - 1);
  const optionId = (key: string) => `${listboxId}-${key}`;
  const activeId = activeIndex >= 0 ? optionId(visible[activeIndex].key) : undefined;

  // Abrir leva o foco à busca; fechar devolve ao botão (o Dropdown do react-bootstrap)
  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  // A opção destacada pelo teclado fica à vista na lista com rolagem
  useEffect(() => {
    if (activeId) document.getElementById(activeId)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeId]);

  // Sair do erro ("Tentar de novo"): o botão some com o foco, que volta à busca (e não ao <body>,
  // onde o Esc e as setas não alcançam o popover)
  const wasErrorRef = useRef(status === 'error');
  useEffect(() => {
    const wasError = wasErrorRef.current;
    wasErrorRef.current = status === 'error';
    if (!wasError || status === 'error') return;
    const focused = document.activeElement;
    const menu = inputRef.current?.closest('.dropdown-menu');
    if (!focused || focused === document.body || menu?.contains(focused)) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [status]);

  // Digitar volta o destaque para a primeira que casa
  const handleQuery = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.currentTarget.value);
    setActiveKey(null);
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    // Shift+Tab sai pela frente do cartão, para o botão: fecha, como o Tab para fora
    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      onBackTab();
      return;
    }
    if (visible.length === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = Math.max(0, Math.min(activeIndex + step, visible.length - 1));
      setActiveKey(visible[next].key);
      setKeyboardActive(true);
      return;
    }
    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      onSelect(visible[activeIndex].key);
    }
  }, [visible, activeIndex, onSelect, onBackTab]);

  let message = '';
  if (status === 'loading') message = loadingMessage;
  else if (status === 'error') message = errorMessage ?? '';
  else if (visible.length === 0) message = emptyMessage;

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        className="my-studies-options-search"
        aria-label={searchLabel}
        aria-expanded={visible.length > 0}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        aria-describedby={message ? messageId : undefined}
        placeholder={searchPlaceholder}
        value={query}
        onChange={handleQuery}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        spellCheck={false}
      />
      <ul id={listboxId} role="listbox" aria-label={listLabel} className="my-studies-options-list">
        {visible.map((item, index) => (
          // O teclado fica no campo de busca (combobox com `aria-activedescendant`): a opção só
          // recebe o clique, e o `mousedown` não tira o foco do campo
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <li
            key={item.key}
            id={optionId(item.key)}
            role="option"
            tabIndex={-1}
            aria-selected={index === activeIndex}
            className={[
              'my-studies-option',
              item.emphasized ? 'is-emphasized' : '',
              item.key === selectedKey ? 'is-current' : '',
              keyboardActive && index === activeIndex ? 'is-active' : '',
            ].filter(Boolean).join(' ')}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onSelect(item.key)}
          >
            <span className="my-studies-option-text">
              <span className="my-studies-option-label">{item.label}</span>
              {item.detail && <span className="my-studies-option-detail">{item.detail}</span>}
            </span>
            {item.count && (
              <span className="my-studies-option-count">
                {item.count}
                <span className="visually-hidden"> estudos</span>
              </span>
            )}
          </li>
        ))}
      </ul>
      <div id={messageId} role="status" className="my-studies-options-message">{message}</div>
      {status === 'error' && onRetry && (
        <Button variant="link" className="my-studies-options-retry" onClick={onRetry}>
          Tentar de novo
        </Button>
      )}
    </>
  );
}

/**
 * Popover com busca e lista de uma escolha só, para os filtros Empresa e Usuário da barra de
 * Meus Estudos (#2000, .dc.html:157-168 e 207-217): o botão abre um cartão com o campo de busca e
 * as opções, e escolher uma fecha o cartão.
 *
 * Sobre o `Dropdown` do react-bootstrap, como o `StatusFilter`: ele posiciona o cartão, fecha no
 * clique fora e no Esc e devolve o foco ao botão quando o cartão fecha com o foco dentro. O campo
 * é `type="text"` de propósito: no `type="search"` o Dropdown ignora o Esc.
 *
 * Teclado: Enter, Espaço ou seta para baixo no botão abrem e levam o foco à busca. Na busca, as
 * setas movem o destaque, Enter escolhe, Esc fecha sem escolher, e Tab ou Shift+Tab para fora
 * fecham.
 *
 * Mouse: clicar numa área do cartão que não recebe foco (borda, vão entre opções, mensagem) não
 * tira o foco da busca; sem isso, ele cairia no <body>, e o Esc e as setas parariam de funcionar
 * com o cartão aberto. O botão é `aria-haspopup="dialog"`: o cartão tem uma busca e uma lista.
 *
 * Controlado por props; a regra de cada lista (ordem, escopo, rótulos) é de quem chama.
 */
export default function OptionsPopover({
  open,
  onOpenChange,
  toggleClassName,
  toggleContent,
  toggleId,
  toggleAriaLabel,
  toggleAriaLabelledBy,
  align = 'start',
  menuClassName = '',
  onSelect,
  ...panelProps
}: OptionsPopoverProps) {
  const toggleRef = useRef<HTMLButtonElement>(null);

  const handleToggle = useCallback((nextShow: boolean) => onOpenChange(nextShow), [onOpenChange]);

  // O foco vai ao botão antes de o cartão fechar: o Dropdown não precisa devolvê-lo, e o foco
  // nunca cai no <body> (o clique na opção não move o foco, que segue no campo de busca)
  const handleSelect = useCallback((key: string) => {
    toggleRef.current?.focus();
    onOpenChange(false);
    onSelect(key);
  }, [onOpenChange, onSelect]);

  // Tab para fora fecha: o Dropdown ignora o Tab que sai de um input. Sem `relatedTarget`
  // (clique numa área sem foco), quem fecha é o clique fora (ver o `StatusFilter`). O foco que
  // vai ao botão é o clique nele, que já alterna o popover.
  const handleMenuBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (!(next instanceof Node)) return;
    if (event.currentTarget.contains(next) || toggleRef.current?.contains(next)) return;
    onOpenChange(false);
  }, [onOpenChange]);

  // Shift+Tab da busca: o foco vai ao botão (o anterior na ordem) e o popover fecha
  const handleBackTab = useCallback(() => {
    toggleRef.current?.focus();
    onOpenChange(false);
  }, [onOpenChange]);

  // Só a busca e os botões do cartão recebem o foco pelo clique; o resto mantém a busca focada
  const keepSearchFocus = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const { target } = event;
    if (target instanceof Element && target.closest('input, button')) return;
    event.preventDefault();
  }, []);

  return (
    <Dropdown show={open} onToggle={handleToggle} autoClose="outside" align={align}>
      <Dropdown.Toggle
        ref={toggleRef}
        as="button"
        type="button"
        id={toggleId}
        className={`my-studies-options-toggle ${toggleClassName}`}
        aria-label={toggleAriaLabel}
        aria-labelledby={toggleAriaLabelledBy}
        aria-haspopup="dialog"
      >
        {toggleContent}
      </Dropdown.Toggle>
      <Dropdown.Menu
        popperConfig={MENU_POPPER_CONFIG}
        className={['my-studies-options-menu', menuClassName].filter(Boolean).join(' ')}
        onBlur={handleMenuBlur}
        onMouseDown={keepSearchFocus}
      >
        {open && (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <OptionsPanel {...panelProps} onSelect={handleSelect} onBackTab={handleBackTab} />
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}
